import { useEffect, useState, useRef } from "react";
import { useTranslation } from 'react-i18next'

// mui
import { Skeleton, Stack } from "@mui/material";
// material
import Question from "./Question";
import Answer from './Answer'
// util
import {
  getLabel,
  getLinkedElements,
  getLinkedOptions,
  getDirectionsLabel,
  getDescription,
  setResult,
  checkResultExists,
  getTranslatedText,
  getLinkedElementsFromMultiple,
  getResultType
} from '../pages/utils_v2/kumuHelper';
// constants
import { QuestionPhase, ElementType, ResultType } from '../constants';
import SearchDesciption from "./SearchDesciption";


// ----------------------------------------------------------------------

export default function KumuQuestionsV2({ currentLegislation, setQuestionPhase, results, setResults, elements, connections }) {
  const { t, i18n } = useTranslation('translation')

  const [questionHistory, setQuestionHistory] = useState([])

  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);

  // New state for tracking navigation history
  const [navigationStack, setNavigationStack] = useState([]);
  const [currentElement, setCurrentElement] = useState(null);

  const [prevElementType, setPrevElementType] = useState([]);
  const [isAnswer, setAnswer] = useState(false);
  const [isMultiple, setIsMultiple] = useState(false)
  const [multiTitle, setMultiTitle] = useState('')
  const [multiDescription ,setMultiDescription] = useState('')

  const [questionText, setQuestionText] = useState("");
  const [questionOptions, setQuestionOptions] = useState([]);

  const ref = useRef(null);

  const handleChange = (e) => {
    const { type, checked, value } = e.target;
    const parsedValue = JSON.parse(value);
    const elementType = parsedValue.value.attributes['element type'];

    if (type === 'radio') {
      // When a single "statement" is selected, clear all multiple selections
      if (elementType === 'statement') {
        setSelectedOptions([]);
      }
      setSelectedOption(parsedValue);
    }

    if (type === 'checkbox') {
      // When a "statement (multiple)" is checked, clear the single selection
      if (elementType === 'statement (multiple)') {
        setSelectedOption(null);
      }
      
      if (checked) {
        setSelectedOptions(prev => [...prev, parsedValue]);
      } else {
        setSelectedOptions(prev => prev.filter(item => item.id !== parsedValue.id));
      }
    }
  };

  useEffect(() => {
    setCurrentElement(currentLegislation)
    setQuestionText(currentLegislation !== undefined ? getDirectionsLabel(currentLegislation, i18n.language) : "")
    setQuestionOptions(getLinkedOptions(connections, elements, currentLegislation))
    
    // Initialize navigation stack with the first question
    if (currentLegislation !== undefined && navigationStack.length === 0) {
      setNavigationStack([{
        currentElement: currentLegislation,
        questionText: getDirectionsLabel(currentLegislation, i18n.language),
        questionOptions: getLinkedOptions(connections, elements, currentLegislation),
        selectedOption: null,
        selectedOptions: [],
        questionHistory: [],
        isAnswer: false,
        prevElementType: null,
        isMultiple: false,
        multiTitle: '',
        multiDescription: ''
      }]);
    }
  }, [currentElement != null]);

  const handleContinue = (e) => {
    let nextElement = {}

    if (prevElementType == ElementType.REASON || prevElementType == ElementType.RESULT){
      if(isMultiple){
          setSelectedOption(null);
          setSelectedOptions([])
          setQuestionPhase(QuestionPhase.LEGISLATIONS)
          setIsMultiple(false)
          // Reset navigation stack when going back to legislations
          setNavigationStack([]);
          return;
      }
      else{
        nextElement = getLinkedElements(connections, elements, currentElement, []) 
      }

    }
    else { 
      nextElement = selectedOptions.length === 0 ? getLinkedElements(connections, elements, selectedOption, []) : getLinkedElementsFromMultiple(connections, elements, selectedOptions, [])
    }

    let newQH = ''
    if(selectedOptions.length === 0){
      newQH = questionHistory.concat(currentLegislation !== undefined ? `[${t('Answer')}: ${getTranslatedText(i18n.language, selectedOption.value.attributes.label)}]` : "")
      setQuestionHistory(newQH)
    }
    else{
        // for multi select
        const s = selectedOptions.map(item => getTranslatedText(i18n.language, item.value.attributes.label)).join(', ');
        newQH = questionHistory.concat(currentLegislation !== undefined ? `[${t('Answer')}: ${s}]` : "")
        setQuestionHistory(newQH)
    }
    // if REASON is next element
    if (nextElement.reason.length == 1 && nextElement.statements.length == 0 && nextElement.result.length == 0) {
      // Save current state BEFORE transitioning to answer
      const currentState = {
        currentElement,
        questionText,
        questionOptions,
        selectedOption,
        selectedOptions,
        questionHistory: [...questionHistory],
        isAnswer: false,
        prevElementType,
        isMultiple: false,
        multiTitle: '',
        multiDescription: ''
      };
      setNavigationStack(prev => [...prev, currentState]);

      const reason = nextElement.reason[0]
      setResults(setResult(
        results,
        currentLegislation.value.attributes.label,
        getResultType(reason.value.attributes['element status']),
        getDescription(reason, i18n.language),
        newQH,
        null,
        i18n.language
      ))
      setCurrentElement(nextElement.reason[0])
      setPrevElementType(ElementType.REASON)
      setAnswer(true)
    }

    // if STATEMENT is next elements
    if (nextElement.reason.length == 0 && nextElement.statements.length > 1 && nextElement.result.length == 0) {
      // Save current state BEFORE moving to next question
      const currentState = {
        currentElement,
        questionText,
        questionOptions,
        selectedOption,
        selectedOptions,
        questionHistory: [...questionHistory],
        isAnswer: false,
        prevElementType,
        isMultiple: false,
        multiTitle: '',
        multiDescription: ''
      };
      setNavigationStack(prev => [...prev, currentState]);

      setCurrentElement(selectedOption)
      setQuestionText(selectedOption !== undefined ? getDirectionsLabel(selectedOption, i18n.language) : "")
      setQuestionHistory(questionHistory.concat(selectedOption !== undefined ? getDirectionsLabel(selectedOption, i18n.language) + '\n' + `[${t('Answer')}: ${getTranslatedText(i18n.language, selectedOption.value.attributes.label)}]` : ""))
      setQuestionOptions(getLinkedOptions(connections, elements, selectedOption))
      setPrevElementType(ElementType.STATEMENT)
      setAnswer(false);
      // Clear selections for the new question
      setSelectedOption(null);
      setSelectedOptions([]);
    }

    // if RESULT exists after when leaving legislation
    if (nextElement.reason.length == 0 && nextElement.statements.length == 0 && nextElement.result.length == 1) {
      // setResults(setResult(
      //   results,
      //   currentLegislation.value.attributes.label,
      //   ResultType.REQUIRED,
      //   null,
      //   questionHistory,
      //   nextElement.result[0],
      //   i18n.language
      // ))
      setSelectedOption(null);
      setSelectedOptions([]);
      setQuestionPhase(QuestionPhase.LEGISLATIONS)
      setPrevElementType(ElementType.RESULT)
      // Reset navigation stack
      setNavigationStack([]);
    }

    // there are no more elements
    if (nextElement.reason.length == 0 && nextElement.statements.length == 0 && nextElement.result.length == 0) {
      if (!checkResultExists(results, currentLegislation.value.attributes.label)) {
        setResults(setResult(
          results,
          currentLegislation.value.attributes.label,
          ResultType.NOTREQUIRED,
          null,
          questionHistory,
          null,
          i18n.language
        ))
      }
      setSelectedOption(null);
      setSelectedOptions([]);
      setQuestionPhase(QuestionPhase.LEGISLATIONS)
      // Reset navigation stack
      setNavigationStack([]);
    }

    // we have used a multiple.
    if (nextElement.reason.length >= 1 && nextElement.statements.length == 0 && nextElement.result.length == 1) {
      // Save current state BEFORE transitioning to answer
      const currentState = {
        currentElement,
        questionText,
        questionOptions,
        selectedOption,
        selectedOptions,
        questionHistory: [...questionHistory],
        isAnswer: false,
        prevElementType,
        isMultiple: false,
        multiTitle: '',
        multiDescription: ''
      };
      setNavigationStack(prev => [...prev, currentState]);

      setIsMultiple(true)
      const temp = [...results]
      let reason_total = ''
      let reason_title = ''
      nextElement.reason.map(item => {
        reason_title +=  getLabel(item, i18n.language) + '<br/>'
        reason_total += '<strong>' + getLabel(item, i18n.language) + '</strong><br/>' + getDescription(item, i18n.language) + '<br/><br/>'
      })

      setResults(
        setResult(
            results,
            currentLegislation.value.attributes.label,
            ResultType.REQUIRED,
            reason_total,
            newQH,
            nextElement.result[0],
            i18n.language
        )
      )

      setAnswer(true)
      setMultiTitle("")
      setMultiDescription(reason_total)
      setPrevElementType(ElementType.REASON)
    }
    window.scrollTo({ top: 250 });
  };

  const handleBack = (e) => {
    // If we're at the first question (navigation stack is empty or has only 1 item), go back to legislations
    if (navigationStack.length <= 1) {
      // Remove any results for this legislation when backing out completely
      // Filter by checking all possible property names that might contain the legislation name
      const legislationName = currentLegislation.value.attributes.label;
      const filteredResults = results.filter(
        result => result.legislation !== legislationName && 
                  result.name !== legislationName &&
                  result.label !== legislationName
      );
      setResults(filteredResults);
      
      setSelectedOption(null);
      setSelectedOptions([]);
      setQuestionHistory([]);
      setAnswer(false);
      setIsMultiple(false);
      setMultiTitle('');
      setMultiDescription('');
      setPrevElementType(null);
      setQuestionPhase(QuestionPhase.LEGISLATIONS);
      setNavigationStack([]);
      return;
    }

    // Otherwise, pop the last state from the stack and restore it
    const newStack = [...navigationStack];
    newStack.pop(); // Remove current state
    const previousState = newStack[newStack.length - 1]; // Get previous state
    
    setNavigationStack(newStack);
    
    // Restore previous state
    setCurrentElement(previousState.currentElement);
    setQuestionText(previousState.questionText);
    setQuestionOptions(previousState.questionOptions);
    setSelectedOption(previousState.selectedOption);
    setSelectedOptions(previousState.selectedOptions);
    setQuestionHistory(previousState.questionHistory);
    setAnswer(previousState.isAnswer);
    setPrevElementType(previousState.prevElementType);
    setIsMultiple(previousState.isMultiple || false);
    setMultiTitle(previousState.multiTitle || '');
    setMultiDescription(previousState.multiDescription || '');
    
    // Remove any results that were added for this legislation
    // This handles backing out from an answer screen
    if (isAnswer) {
      const legislationName = currentLegislation.value.attributes.label;
      const filteredResults = results.filter(
        result => result.legislation !== legislationName && 
                  result.name !== legislationName &&
                  result.label !== legislationName
      );
      setResults(filteredResults);
    }
  };

  return (
    <div>
      {currentLegislation !== undefined ? (
        <>
          {isAnswer ?
            isMultiple?
            <>
            <SearchDesciption desc={<p>{t("To explore other laws and regulations that may apply to your project, click 'Continue'")}</p>} />
            <Answer
              key={currentElement?.id}
              activity={getTranslatedText(i18n.language, currentLegislation.value.attributes.label)}
              header={multiTitle}
              answerText={multiDescription}
            />
            </> : <>
            <SearchDesciption desc={<p>{t("To explore other laws and regulations that may apply to your project, click 'Continue'")}</p>} />
            <Answer
              key={currentElement?.id}
              activity={getTranslatedText(i18n.language, currentLegislation.value.attributes.label)}
              header={currentElement !== undefined ? getLabel(currentElement, i18n.language) : ""}
              answerText={currentElement !== undefined ? getDescription(currentElement, i18n.language) : ""}
            />
            </>
            :
            <>
            <SearchDesciption desc={<p>{t("Select the statements below that apply to your project and click ‘Continue’ to find out your responsibilities under this legislation. To explore other laws and regulations that may apply to your project, click ‘Back’ to return to the relevant legislation list.")}</p>} />
            <Question
              activity={currentLegislation.length !== 0 ? currentLegislation.value.attributes.label : currentLegislation}
              questionText={questionText}
              options={questionOptions}
              handleChange={handleChange}
              selectedOption={selectedOption}
              selectedOptions={selectedOptions}
            />
            </>
          }

          <br />
          <div className="left">
            <button
              className="btn btn-default wide-button"
              onClick={(e) => handleBack(e)}
            >
              {t('Back')}
            </button>
            <div className="divider" />
            <button
              className="btn btn-primary wide-button"
              onClick={(e) => { handleContinue(e); ref.current?.scrollTo(0, 0); }}
              disabled={!isAnswer && !selectedOption && selectedOptions.length === 0}
            >
              {t('Continue')}
            </button>
          </div>
        </>
      ) : (
        <Stack spacing={2}>
          <Skeleton variant="text" height={75} />
          <Stack direction="row" spacing={1}>
            <Skeleton variant="circular" width={20} height={20} />
            <Skeleton variant="rectangular" width={250} height={20} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <Skeleton variant="circular" width={20} height={20} />
            <Skeleton variant="rectangular" width={250} height={20} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <Skeleton variant="circular" width={20} height={20} />
            <Skeleton variant="rectangular" width={250} height={20} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <Skeleton variant="circular" width={20} height={20} />
            <Skeleton variant="rectangular" width={250} height={20} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <Skeleton variant="circular" width={20} height={20} />
            <Skeleton variant="rectangular" width={250} height={20} />
          </Stack>
        </Stack>
      )}
    </div>
  );
}